import React, { useState, useEffect } from 'react';
import UserEntryList from './UserEntryList';
import UserAddEntryForm from './UserAddEntryForm';
import UserGoalCheckCalendar from './UserGoalCheckCalendar';
import '../Style/goalCard.css';
import '../Style/category.css';
import '../Style/GoalCard/gC-MarkDoneBtn.css';
import '../Style/GoalCard/gC-Menu.css';
import '../Style/GoalCard/gC-calendar.css';
import { GoalCategoryTag, GoalCategoryPickerModal } from '../usercomponents/GoalCategory';
import { createPortal } from 'react-dom';
import CharCounter from '../components/CharCounter';

function UserGoalCard({
  goal,
  isOpen,
  selectedGoal,
  onView,
  onDelete,
  onRename,
  onMarkDoneToday,
  onToggleArchive,
  onDifficultyChange,
  checkDates,
  newEntryDescription,
  onChangeNewEntry,
  onAddEntry,
  onDeleteEntry,
  onRenameEntry,
  dragHandleProps,
  isArchived,
  handleToggleAchievement,
  isAchievementPage,
  viewedMonth,
  onPrevMonth,
  onNextMonth,
  doneToday,
  isReadOnly,
  isDoneToday: isDoneProp,
  onUpdateCategory,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false); // New state for modal
  const handleMenuToggle = () => setMenuOpen((prev) => !prev);
  const isDoneToday = isDoneProp === true || doneToday === true;
  // Add these to your other useState hooks
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(goal.goalTitle || goal.title);
  const TITLE_MAX = 100;

  // Handle saving from the modal
  const handleCategorySave = (selection) => {
    if (onUpdateCategory) {
      onUpdateCategory(goal.id, selection.category, selection.useCase);
    }
    setShowPicker(false);
  };

  const handleSaveTitle = () => {
    if (tempTitle.trim() !== '' && tempTitle !== (goal.goalTitle || goal.title)) {
      // Assuming onRename is the function that handles the backend call
      // If onRename usually opens a popup, you might need to ensure it accepts (id, newTitle)
      onRename(goal.id, tempTitle);
    }
    setIsEditingTitle(false);
  };

  const diffStyles = {
    // Current Home.jsx uses numbers
    1: { label: 'Easy', color: '#73abff', xp: 5 },
    2: { label: 'Medium', color: '#ffca6e', xp: 15 },
    3: { label: 'Hard', color: '#fe6262', xp: 30 },
    // PublicProfile.jsx will use these Strings from the Enum
    EASY: { label: 'Easy', color: '#73abff', xp: 5 },
    MEDIUM: { label: 'Medium', color: '#ffca6e', xp: 15 },
    HARD: { label: 'Hard', color: '#fe6262', xp: 30 },
  };

  // This line is the "brain" that picks the right style
  const currentDiff = diffStyles[goal.difficulty] || diffStyles[4];

  const displayDate =
    viewedMonth?.year && (viewedMonth?.month || viewedMonth?.month === 0)
      ? new Date(viewedMonth.year, viewedMonth.month)
      : new Date(); // Fallback to current date

  const monthYearString = displayDate.toLocaleString('default', {
    month: 'long',
    year: 'numeric',
  });

  // Inside UserGoalCard.jsx
  const [isAnimate, setIsAnimate] = useState(false);

  const handleMarkDone = () => {
    if (!isDoneToday) {
      setIsAnimate(true);
      // Remove the class after animation finishes so it can trigger again
      setTimeout(() => setIsAnimate(false), 500);
    }
    onMarkDoneToday(goal.id, goal.difficulty);
  };

  return (
    <div
      className={`goal-card glass-card ${isDoneToday ? 'goal-done' : ''} ${
        !isReadOnly && goal.isPrivate ? 'goal-ghost-private' : ''
      }`}
      style={{ '--accent': currentDiff.color, zIndex: menuOpen || showPicker ? 101 : 1 }}
    >
      {/* TOP ROW: Title & Difficulty */}
      <div className="goal-card-header">
        <div className="goal-info">
          <GoalCategoryTag category={goal.category} useCase={goal.useCase} />
          {isEditingTitle && !isReadOnly && !isAchievementPage ? (
            <div className="inline-title-wrapper">
              <input
                autoFocus
                className={`inline-goal-title-input ${tempTitle.length >= TITLE_MAX ? 'input-error' : ''}`}
                value={tempTitle}
                maxLength={TITLE_MAX}
                onChange={(e) => setTempTitle(e.target.value)}
                onBlur={() => {
                  setTempTitle(goal.goalTitle || goal.title);
                  setIsEditingTitle(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSaveTitle();
                  if (e.key === 'Escape') {
                    setTempTitle(goal.goalTitle || goal.title);
                    setIsEditingTitle(false);
                  }
                }}
              />
              <CharCounter current={tempTitle.length} max={TITLE_MAX} />
            </div>
          ) : (
            <h3
              data-testid="goal-title"
              className={`goal-title-text ${!isReadOnly ? 'editable-title' : ''}`}
              onClick={() => !isReadOnly && !isAchievementPage && setIsEditingTitle(true)}
            >
              {goal.goalTitle || goal.title}
              {goal.achievement && <span className="achievement-star"> ⭐</span>}
            </h3>
          )}

          <span className="difficulty-tag" style={{ color: '#2e2e2e' }}>
            {currentDiff.label}
          </span>
        </div>

        <div className="goal-controls">
          {/* Drag Handle - Only shows if not archived */}

          {!isReadOnly && (
            <button className="control-icon-wrapper btn-icon-dots" onClick={handleMenuToggle}
            data-testid="menu-toggle">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="1" />
                <circle cx="12" cy="5" r="1" />
                <circle cx="12" cy="19" r="1" />
              </svg>
            </button>
          )}

          {!isReadOnly && !isArchived && dragHandleProps && (
            <div {...dragHandleProps} className="control-icon-wrapper drag-handle-alt">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="12" r="1" />
                <circle cx="9" cy="5" r="1" />
                <circle cx="9" cy="19" r="1" />
                <circle cx="15" cy="12" r="1" />
                <circle cx="15" cy="5" r="1" />
                <circle cx="15" cy="19" r="1" />
              </svg>
            </div>
          )}
        </div>

        {/* DROPDOWN MENU */}
        {menuOpen && (
          <div className="goal-menu-dropdown" data-testid='menu-dropdown'>
            {/* 1. THE ONLY BUTTON VISIBLE IN ACHIEVEMENTS */}
            {!isArchived && (
              <button
                onClick={() => {
                  handleToggleAchievement(goal.id);
                  setMenuOpen(false);
                }}
              >
                {goal.achievement || isAchievementPage ? 'Unmark Achievement' : 'Mark Achievement'}
              </button>
            )}
            {!isReadOnly && !isAchievementPage && (
              <>
                {/* Category */}
                <button
                  onClick={() => {
                    setShowPicker(true);
                    setMenuOpen(false);
                  }}
                >
                  {goal.category ? 'Edit Category' : 'Set Category'}
                </button>

                {/* Rename */}
                {onRename && (
                  <button
                    onClick={() => {
                      setIsEditingTitle(true);
                      setMenuOpen(false);
                    }}
                  >
                    Rename
                  </button>
                )}

                {/* Archive */}
                {onToggleArchive && (
                  <button
                    onClick={() => {
                      onToggleArchive(goal.id);
                      setMenuOpen(false);
                    }}
                  >
                    {isArchived ? 'Restore' : 'Archive'}
                  </button>
                )}

                {/* Difficulty Select (if you have it) */}
                {!isArchived && onDifficultyChange && (
                  <div className="menu-select-group">
                    <label>Level</label>
                    <select
                      value={
                        typeof goal.difficulty === 'string'
                          ? { EASY: 1, MEDIUM: 2, HARD: 3 }[goal.difficulty]
                          : goal.difficulty
                      }
                      disabled={doneToday}
                      onChange={(e) => onDifficultyChange(goal.id, parseInt(e.target.value))}
                    >
                      <option value={1}>Easy</option>
                      <option value={2}>Medium</option>
                      <option value={3}>Hard</option>
                    </select>
                  </div>
                )}

                {/* Delete */}
                {onDelete && (
                  <button 
                  data-testid="card-delete"
                  className="delete-text" onClick={() => onDelete(goal.id)}>
                    Delete
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* MIDDLE ROW: Main Action Buttons */}
      <div className="goal-card-actions">
        <button
          className="view-details-btn"
          onClick={() => onView(goal.id)}
          style={{ padding: '8px 24px', width: 'auto', minHeight: '40px' }}
        >
          {isOpen ? 'Hide Progress' : 'View Progress'}
        </button>

        {/* 1. VISITOR VIEW: Show Badge */}
        {isReadOnly && isDoneToday && (
          <div className="status-badge completed">✓ Completed Today</div>
        )}

        {/* 2. OWNER VIEW: Show Working Button */}
        {!isReadOnly && !isArchived && onMarkDoneToday && (
          <button
            className={`mark-done-btn ${isDoneToday ? 'completed' : ''} ${isAnimate ? 'pulse-click' : ''}`}
            onClick={handleMarkDone}
            style={{ backgroundColor: isDoneToday ? '#10b981' : currentDiff.color }}
          >
            {isDoneToday ? '✓ Done Today' : 'Mark Complete'}
          </button>
        )}
      </div>

      {isOpen && (goal || selectedGoal) && (
        <div className="goal-expanded-content">
          {/* TOP PART: Side-by-Side Calendar and Media */}
          <div className="expanded-layout">
            {/* LEFT: Calendar only */}
            <div className="expanded-main-col">
              <div className="month-nav">
                <button className="nav-arrow-btn" onClick={() => onPrevMonth(goal.id)}>
                  <span>‹</span>
                </button>
                <div className="month-display">
                  <span className="month-label">{monthYearString}</span>
                </div>
                <button className="nav-arrow-btn" onClick={() => onNextMonth(goal.id)}>
                  <span>›</span>
                </button>
              </div>

              <UserGoalCheckCalendar
                checkDates={checkDates}
                month={viewedMonth?.month}
                year={viewedMonth?.year}
              />
            </div>

            {/* RIGHT: Future Media Zone - <span>Photos Coming Soon...</span>*/}
            <div className="expanded-media-col">
              <div className="photo-placeholder"></div>
            </div>
          </div>

          {/* BOTTOM PART: Full Width Entries */}
          <div className="full-width-entries">
            <h4 className="section-title">Notes</h4>

            <UserEntryList
              entries={selectedGoal?.entries || goal?.entries || []}
              onDeleteEntry={onDeleteEntry}
              onRenameEntry={onRenameEntry}
              isReadOnly={isReadOnly || isAchievementPage}
              selectedGoal={{
                ...goal,
                entries: goal.entries || [], // Make sure this is explicitly set
              }}
            />

            {!isAchievementPage && !isReadOnly && (
              <UserAddEntryForm
                value={newEntryDescription || ''} // Fallback to empty string just in case
                onChange={onChangeNewEntry}
                onAddEntry={onAddEntry}
              />
            )}
          </div>
        </div>
      )}

      {showPicker &&
        createPortal(
          <GoalCategoryPickerModal
            isOpen={showPicker}
            onClose={() => setShowPicker(false)}
            onSelect={handleCategorySave}
            currentCategory={goal.category}
            useCase={goal.useCase}
          />,
          document.body, // renders outside the card's stacking context entirely
        )}
    </div>
  );
}

export default UserGoalCard;
